// ============================================================================
// LOT_ROLES_HUB / LOT_DEMO — l'équipe PharmaGestion et les commerciaux
// ============================================================================
// Ni l'équipe ni les commerciaux n'ont de compte Officina : leur niveau vient du
// hub, et n'existe que sur une session Clerk (`rh.pharmagestion.fr`). Tout part de
// `onboarding-status`, qui dit AVANT `/api/auth/me` qui est là :
//   - équipe (SUPER_ADMIN, ADMIN) : console, et l'intervention en cours s'il y en a une ;
//   - commercial : sa démo « Pharmacie du Bourg » est-elle ouverte ?
// Pendant une intervention ou une démo, `/api/auth/me` renvoie le titulaire de
// l'officine visée : l'application normale s'affiche, sous un bandeau.
// ============================================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, auth } from './client';
import { authApi } from './auth';
import { hasClerkSession } from '@/lib/clerkSso';

export type RolePlateforme = 'SUPER_ADMIN' | 'ADMIN' | 'COMMERCIAL';

export interface EtatPlateforme {
  role: RolePlateforme;
  email: string;
  intervention: {
    id: string;
    reason: string;
    startedAt: string;
    pharmacy: { id: string; name: string; city: string | null };
  } | null;
  demo: { active: boolean; startedAt: string | null } | null;
}

export interface OfficineConsole {
  id: string;
  name: string;
  city: string | null;
  group: { id: string; name: string };
  titulaires: string[];
  employees: number;
  /** Options payantes — posées ici, par l'équipe, jamais par le titulaire. */
  options: { gestionSociale: boolean; socialEmail: string | null };
}

export interface LigneIntervention {
  id: string;
  actorEmail: string;
  actorRole: string;
  reason: string;
  startedAt: string;
  endedAt: string | null;
  pharmacy: { id: string; name: string };
}

export function estEquipe(role: RolePlateforme | undefined | null): boolean {
  return role === 'SUPER_ADMIN' || role === 'ADMIN';
}

/** L'état plateforme de la session Clerk (null hors hub, ou pour un client ordinaire). */
export function usePlateforme() {
  return useQuery({
    queryKey: ['auth', 'onboarding'],
    queryFn: () => authApi.onboardingStatus(),
    enabled: hasClerkSession(),
    retry: false,
    staleTime: 30 * 1000,
    select: (d) => d.platform ?? null,
  });
}

/**
 * Après une bascule (intervention ouverte ou close, démo lancée ou quittée), tout
 * ce qui a été lu appartient à une autre officine : on repart d'un écran neuf.
 * L'officine active mémorisée (multi-site) est oubliée — elle visait l'ancienne.
 */
function repartir(vers: string) {
  auth.clearSite();
  window.location.href = vers;
}

export function useOfficinesConsole() {
  return useQuery({
    queryKey: ['platform', 'pharmacies'],
    queryFn: () => api.get<{ pharmacies: OfficineConsole[] }>('/api/platform/pharmacies'),
    select: (d) => d.pharmacies,
  });
}

export function useOptionsOfficine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { pharmacyId: string; gestionSociale?: boolean; socialEmail?: string | null }) => {
      const { pharmacyId, ...options } = b;
      return api.patch<{ options: OfficineConsole['options'] }>(`/api/platform/pharmacies/${pharmacyId}/options`, options);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'pharmacies'] }),
  });
}

export function useJournalInterventions() {
  return useQuery({
    queryKey: ['platform', 'interventions'],
    queryFn: () => api.get<{ interventions: LigneIntervention[] }>('/api/platform/interventions'),
    select: (d) => d.interventions,
  });
}

export function useOuvrirIntervention() {
  return useMutation({
    mutationFn: (b: { pharmacyId: string; reason: string }) => api.post('/api/platform/interventions', b),
    onSuccess: () => repartir('/dashboard'),
  });
}

export function useFermerIntervention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/platform/interventions/end'),
    onSuccess: () => {
      qc.clear();
      repartir('/console');
    },
  });
}

export function useLancerDemo() {
  return useMutation({
    mutationFn: () => api.post<{ success: true; pharmacyId: string }>('/api/platform/demo/start'),
    onSuccess: () => repartir('/dashboard'),
  });
}

export function useQuitterDemo(retour: string) {
  return useMutation({
    mutationFn: () => api.post('/api/platform/demo/leave'),
    onSettled: () => repartir(retour),
  });
}
